/**
 * „Theme sync": cum află TEMA magazinului că noi i-am schimbat coșul pe la spate.
 *
 * Problema, văzută live pe demo3 (Horizon, 9 aug 2026): noi mutăm coșul prin Ajax
 * Cart API (`shopify.ts`), dar sertarul de coș al temei e HTML randat de Liquid la
 * încărcarea paginii. Un `fetch` nu-l atinge: badge-ul îl scriem noi, sertarul
 * rămâne cu lista veche până la un reload. Reload-ul îl aveam deja ca plasă
 * (`maybeRefreshCartPage`), dar el rezolvă doar PAGINA de coș - nu sertarul, și îl
 * plătim cu o navigare întreagă.
 *
 * Ce am găsit citind sursa temei Horizon (Admin API, tema MAIN a magazinului):
 *
 *   - `assets/component-cart-items.js` și `assets/cart-icon.js` ascultă pe
 *     `document` evenimentul `StandardEvents.cartLinesUpdate` din modulul de
 *     platformă `@shopify/events`
 *     (`https://cdn.shopify.com/storefront/standard-events.js`, mapat în
 *     `snippets/scripts.liquid`). Numele lui pe sârmă: `shopify:cart:lines-update`.
 *   - Evenimentul e un `Event` obișnuit (bubbles + cancelable) peste care clasa
 *     face `Object.assign(this, data)` - deci `action`, `context`, `lines` și
 *     `promise` stau DIRECT pe eveniment, NU sub `detail`. Reproducem forma cu un
 *     `Event` simplu, fiindcă SDK-ul nu poate importa modulul temei.
 *   - `promise` e contractul propriu-zis: ascultătorii îl așteaptă și citesc
 *     `{ cart, detail }`. Dacă `detail.sections[sectionId]` există, tema face
 *     `morphSection(...)` cu HTML-ul primit; dacă NU există, tema își reia singură
 *     secțiunea prin Section Rendering API
 *     (`sectionRenderer.renderSection(sectionId, { cache: false })`).
 *     Ambele ramuri sunt bune pentru noi - a doua e chiar plasa de siguranță
 *     scrisă de Shopify, gratis.
 *   - `assets/cart-icon.js` ia numărul din `cart?.totalQuantity ?? detail?.itemCount`,
 *     deci badge-ul temei se corectează singur dacă îi dăm `itemCount`.
 *   - Sertarul (`snippets/cart-drawer.liquid`, montat din `layout/theme.liquid` pe
 *     toate template-urile în afară de `cart`) e secțiunea `cart-drawer-section`;
 *     pagina de coș e `sections/main-cart.liquid`. NU hardcodăm niciunul: ambele
 *     își pun id-ul pe `cart-items-component[data-section-id]`, exact de unde îl
 *     citește și tema când își compune propriul `sections=...`.
 *
 * De ce e strategia asta generică, nu „un hack pentru Horizon": evenimentul e al
 * PLATFORMEI (Storefront Standard Events), nu al temei. Orice temă care îl ascultă
 * se sincronizează, iar una care nu-l ascultă nu se strică - un eveniment pe
 * `document` pe care nimeni nu-l prinde nu face nimic.
 */

/**
 * Marca temelor care se pot sincroniza: elementul pe care Horizon (și temele
 * Shopify moderne construite pe același contract) îl pune în jurul listei de coș,
 * purtându-și id-ul de secțiune. E și sursa id-urilor pe care le cerem în
 * răspunsul mutației, și semnalul „tema asta o știm" pe baza căruia decidem că
 * reload-ul paginii de coș nu mai e necesar.
 */
export const CART_SECTION_SELECTOR = "cart-items-component[data-section-id]";

/** Numele pe sârmă al `StandardEvents.cartLinesUpdate` (Storefront Standard Events). */
export const CART_LINES_UPDATE_EVENT = "shopify:cart:lines-update";

/** Ce fel de mutație raportăm temei - aceleași trei valori pe care le emite ea însăși. */
export type CartMutationAction = "add" | "update" | "remove";

export interface ThemeSyncInput {
  action: CartMutationAction;
  /**
   * Platforma magazinului. Absent = `shopify`, ca tot ce s-a scris înainte de
   * 21 aug 2026: strategiile Shopify rămân singurele încercate, byte-identic.
   * Strategiile sunt filtrate pe platformă, nu doar ordonate - un eveniment
   * Shopify emis pe un WordPress ar fi inofensiv, dar și complet inutil, iar
   * `handled` (care stinge reload-ul paginii de coș) nu are voie să vină de la
   * o strategie care n-are ce sincroniza.
   */
  platform?: "shopify" | "woocommerce";
  /** Varianta atinsă; devine `merchandiseId` doar pe `add` (vezi `buildLines`). */
  variantId: string;
  quantity: number;
  /** Secțiunile venite CHIAR în răspunsul mutației (`sections=` pe /cart/add.js). */
  sections?: Record<string, string>;
  /** Numărul proaspăt de produse, din /cart.js - de aici își ia tema badge-ul. */
  itemCount?: number;
}

/**
 * Id-urile de secțiune pe care tema le-ar re-randa ea însăși după o mutație.
 *
 * Le citim ÎNAINTE de mutație ca să le putem cere direct în corpul lui
 * `/cart/add.js` (`sections=a,b`): un singur drum dus-întors în loc de mutație +
 * fetch separat de secțiuni. Dacă nu găsim niciunul, nu cerem nimic - tema
 * necunoscută nu plătește un parametru inutil.
 */
export function cartSectionIds(): string[] {
  const ids = new Set<string>();
  try {
    for (const node of document.querySelectorAll(CART_SECTION_SELECTOR)) {
      const id = (node as HTMLElement).dataset.sectionId;
      // Un `data-section-id` gol apare când snippetul e randat fără parametru;
      // trimis mai departe ar strica tot parametrul `sections`.
      if (id) ids.add(id);
    }
  } catch {
    return [];
  }
  return [...ids];
}

/**
 * `lines` în forma cerută de Storefront Standard Events.
 *
 * Doar pe `add`: acolo cheia e `merchandiseId` și e chiar variant id-ul pe care
 * îl avem din catalog, deci GID-ul e adevărat. Pe `update`/`remove` cheia ar fi
 * `id`-ul LINIEI de coș (`gid://shopify/CartLine/<key>?cart=<token>`) - noi nu
 * avem cheia de linie, lucrăm cu variant id. Un GID inventat ar fi mai rău decât
 * absența câmpului: niciun ascultător din temă nu-l citește, dar un pixel de
 * analytics l-ar înghiți ca adevăr. Deci îl omitem, explicit.
 */
function buildLines(input: ThemeSyncInput): Array<Record<string, unknown>> | undefined {
  if (input.action !== "add") return undefined;
  return [{ quantity: input.quantity, merchandiseId: `gid://shopify/ProductVariant/${input.variantId}` }];
}

/** O strategie de sincronizare = o familie de teme. Prima care raportează „am prins-o" oprește lista. */
interface ThemeSyncStrategy {
  name: string;
  /** Platforma căreia îi vorbește strategia. */
  platform: "shopify" | "woocommerce";
  /** `true` doar dacă tema chiar e din familia asta - altfel trecem mai departe. */
  run: (input: ThemeSyncInput) => boolean;
}

/**
 * Storefront Standard Events - Horizon și temele Shopify moderne.
 *
 * Emitem evenimentul ÎNTOTDEAUNA (e standard de platformă și e inofensiv dacă
 * nimeni nu ascultă), dar raportăm „prins" doar când am văzut măcar o secțiune de
 * coș în DOM. Distincția contează: `handled = true` e ce stinge reload-ul paginii
 * de coș, iar o presupunere greșită acolo ar lăsa vizitatorul cu un coș vechi pe
 * ecran.
 *
 * `action: "add"` are un efect secundar asumat: `assets/cart-drawer.js` deschide
 * sertarul dacă merchantul a bifat `auto-open`. E reacția LUI normală la o
 * adăugare în coș, aceeași pe care o dă butonului propriu - nu o inventăm noi, și
 * o lăsăm și în conversația vocală, unde un `<dialog>` care se deschide nu taie
 * apelul (spre deosebire de reload, care de-aia e oprit acolo).
 */
const shopifyStandardEvents: ThemeSyncStrategy = {
  name: "shopify-standard-events",
  platform: "shopify",
  run(input) {
    const known = cartSectionIds().length > 0;
    const event = new Event(CART_LINES_UPDATE_EVENT, { bubbles: true, cancelable: true });
    const lines = buildLines(input);
    Object.assign(event, {
      action: input.action,
      context: "cart",
      ...(lines ? { lines } : {}),
      /**
       * Rezolvată imediat: mutația s-a încheiat deja când ajungem aici.
       * `cart: null` e deliberat - forma completă e cea din SFAPI
       * (`totalQuantity`, `cost`, `lines` cu GID-uri de linie), pe care n-o putem
       * construi onest din `/cart.js`; ascultătorii temei o citesc cu `?.` și cad
       * pe `detail.itemCount`, pe care îl avem exact.
       * Fără `sections`, tema își reia singură secțiunea - fallback-ul ei, nu al nostru.
       */
      promise: Promise.resolve({
        cart: null,
        detail: {
          ...(input.sections ? { sections: input.sections } : {}),
          ...(typeof input.itemCount === "number" ? { itemCount: input.itemCount } : {}),
          source: "ai-guidance",
          didError: false,
        },
      }),
    });
    document.dispatchEvent(event);
    return known;
  },
};

/**
 * Warehouse (familia veche, pre-Horizon) - tema demo3.
 *
 * Sursa n-a venit din Admin API (tema e trial, fișierele-s blocate până la
 * cumpărare), ci din `theme.js`-ul COMPILAT, public pe CDN-ul storefront-ului -
 * citit pe 12 aug 2026, `schema_version` 4.4.1. Ce ascultă tema, delegat pe
 * `document.documentElement`:
 *
 *   - `product:added` - CustomEvent cu `detail: { button, variant, quantity,
 *     productAdded }`. Handler-ul header-ului își incrementează badge-ul cu
 *     `detail.quantity`, re-randează mini-cart-ul (fetch de HTML proaspăt) și,
 *     dacă `productAdded` și `cartType === "drawer"`, DESCHIDE sertarul - exact
 *     reacția lui la butonul propriu de adăugare, ca la Horizon cu auto-open.
 *     `button: null` e sigur: singura citire (`button.innerHTML`, pe cartType
 *     „message") e gardată cu `&&`.
 *   - `cart:refresh` - re-randează mini-cart-ul fără să-l deschidă;
 *     `detail.scrollToTop: false` ca să nu sară pagina. Pentru update/remove.
 *
 * Pe pagina de COȘ tema nu ascultă niciunul dintre ele (singurul listener
 * `cart:refresh` e al mini-cart-ului din header; fluxul ei propriu face redirect
 * întreg pe `pageType === "cart"`) - deci acolo raportăm „neprins" și lăsăm
 * reload-ul din `maybeRefreshCartPage` să facă ce făcea.
 */
const warehouse: ThemeSyncStrategy = {
  name: "warehouse",
  platform: "shopify",
  run(input) {
    const w = window as unknown as {
      Shopify?: { theme?: { schema_name?: string } };
      theme?: { pageType?: string };
    };
    if (w.Shopify?.theme?.schema_name !== "Warehouse") return false;
    if (input.action === "add") {
      document.body.dispatchEvent(
        new CustomEvent("product:added", {
          bubbles: true,
          detail: { button: null, variant: null, quantity: input.quantity, productAdded: true },
        }),
      );
    } else {
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", { bubbles: true, detail: { scrollToTop: false } }),
      );
    }
    return w.theme?.pageType !== "cart";
  },
};

/**
 * WooCommerce - fragmentele de coș.
 *
 * Contractul e al PLATFORMEI, nu al temei, exact ca la Shopify, doar că aici e
 * mai vechi și mai simplu: `assets/js/frontend/cart-fragments.js` (livrat de
 * WooCommerce, nu de temă) ascultă pe `document.body` evenimentul jQuery
 * `wc_fragment_refresh`, reia `get_refreshed_fragments` prin AJAX și înlocuiește
 * fiecare bucată de HTML declarată în `woocommerce_add_to_cart_fragments` -
 * coșul din header, sertarul temei, totalul. Storefront (tema magazinului
 * nostru de test) își declară acolo `.widget_shopping_cart_content` și contorul
 * din header.
 *
 * DOUĂ dispecerizări, fiindcă sunt două lumi:
 *   - jQuery, dacă există: `wc_fragment_refresh` e un eveniment jQuery, iar
 *     handlerele înregistrate cu `jQuery(...).on(...)` NU se declanșează la un
 *     `dispatchEvent` nativ. jQuery e prezent pe temele clasice tocmai fiindcă
 *     WooCommerce îl cere.
 *   - eveniment DOM nativ ca plasă, pentru codul care ascultă fără jQuery.
 *
 * Pentru temele BLOCK (coșul randat din `@woocommerce/block-library`, fără
 * fragmente) invalidăm în schimb store-ul de date: `wp.data.dispatch('wc/store/cart')
 * .invalidateResolutionForStore()` face blocurile să-și reia coșul din Store API -
 * același API pe care tocmai l-am folosit noi.
 *
 * `handled` e adevărat doar dacă am găsit CU CE să sincronizăm (jQuery sau
 * `wp.data`); altfel rămâne false și pagina de coș se reîncarcă, ca la o temă
 * Shopify necunoscută. NETESTAT PE O TEMĂ BLOCK reală - magazinul nostru de
 * test e Storefront, temă clasică (vezi docs/plan-commerce-actions.md).
 */
const wooFragments: ThemeSyncStrategy = {
  name: "woo-fragments",
  platform: "woocommerce",
  run() {
    let handled = false;
    const w = window as unknown as {
      jQuery?: ((target: unknown) => { trigger: (name: string) => void }) | undefined;
      wp?: { data?: { dispatch?: (store: string) => { invalidateResolutionForStore?: () => void } | undefined } };
    };
    const jq = w.jQuery;
    if (typeof jq === "function" && document.body) {
      jq(document.body).trigger("wc_fragment_refresh");
      handled = true;
    } else if (document.body) {
      document.body.dispatchEvent(new Event("wc_fragment_refresh", { bubbles: true }));
    }
    const store = w.wp?.data?.dispatch?.("wc/store/cart");
    if (store?.invalidateResolutionForStore) {
      store.invalidateResolutionForStore();
      handled = true;
    }
    return handled;
  },
};

/**
 * Lista de adaptoare de temă, încercate în ordine. Standard Events rămâne
 * primul: emite mereu (inofensiv) și „prinde" doar pe temele cu secțiuni de coș
 * în DOM - pe Warehouse întoarce false și se trece mai departe.
 */
const STRATEGIES: ThemeSyncStrategy[] = [shopifyStandardEvents, warehouse, wooFragments];

/**
 * Anunță tema că i-am schimbat coșul. Întoarce `true` doar dacă o strategie a
 * recunoscut tema - adică dacă sertarul/pagina chiar se vor re-randa singure.
 *
 * Nu aruncă niciodată: sincronizarea e un bonus peste o mutație care a REUȘIT
 * deja: o eroare aici n-are voie să transforme o adăugare bună într-un toast roșu.
 */
export function syncThemeCart(input: ThemeSyncInput): boolean {
  const platform = input.platform ?? "shopify";
  for (const strategy of STRATEGIES) {
    if (strategy.platform !== platform) continue;
    try {
      if (strategy.run(input)) return true;
    } catch {
      // Tema următoare din listă poate încă reuși; o strategie care aruncă e un bug al ei, nu al nostru.
    }
  }
  return false;
}
